import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ScrollToTop } from "@/components/ScrollToTop";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ManagerDashboard from "./pages/ManagerDashboard";
import ManagerDashboardV2 from "./pages/ManagerDashboardV2";
import RepDashboard from "./pages/RepDashboard";
import Today from "./pages/Today";
import DealDetail from "./pages/DealDetail";
import DealsList from "./pages/DealsList";
import QuickLog from "./pages/QuickLog";
import Leaderboard from "./pages/Leaderboard";
import Reviews from "./pages/Reviews";
import SettingsPage from "./pages/Settings";
import Programs from "./pages/Programs";
import ProgramDetail from "./pages/ProgramDetail";
import WishlistPage from "./pages/Wishlist";
import Roadmap from "./pages/Roadmap";
import PilotHealth from "./pages/PilotHealth";
import NotFound from "./pages/NotFound";
import EmailPreview from "./pages/EmailPreview";
import ResetPassword from "./pages/ResetPassword";
import UnsubscribePage from "./pages/Unsubscribe";
import CsiSubmit from "./pages/CsiSubmit";
import Pricing from "./pages/Pricing";
import Terms from "./pages/Terms";
import Refunds from "./pages/Refunds";
import Privacy from "./pages/Privacy";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import { PaymentTestModeBanner } from "./components/PaymentTestModeBanner";
import { TrialEndingBanner } from "./components/TrialEndingBanner";
import { DashboardPaywallGate } from "./components/DashboardPaywallGate";
import { BillingNotifications } from "./components/BillingNotifications";

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function ManagerRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, role, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role !== 'manager' && role !== 'admin') return <Navigate to="/rep" replace />;
  return <>{children}</>;
}

function RepRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, role, loading, isSolo } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Managers/admins go to their dashboard — except solo users (single-person
  // workspaces hold both manager + rep roles and intentionally use /rep).
  if (!isSolo && (role === 'manager' || role === 'admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function DashboardRoutes() {
  return (
    <ManagerRoute>
      <DashboardPaywallGate><DashboardLayout>
        <Routes>
          <Route path="/" element={<ManagerDashboard />} />
          <Route path="v2" element={<ManagerDashboardV2 />} />
          <Route path="health" element={<PilotHealth />} />
          <Route path="/deals" element={<DealsList />} />
          <Route path="/deals/:id" element={<DealDetail />} />
          <Route path="/log-activity" element={<QuickLog />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/rep" element={<RepDashboard />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </DashboardLayout></DashboardPaywallGate>
    </ManagerRoute>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PaymentTestModeBanner />
          <TrialEndingBanner />
          <BillingNotifications />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/csi/:token" element={<CsiSubmit />} />
            <Route path="/email-preview" element={<EmailPreview />} />
            <Route path="/dashboard/*" element={<DashboardRoutes />} />
            <Route path="/deals" element={<ManagerRoute><DashboardPaywallGate><DashboardLayout><DealsList /></DashboardLayout></DashboardPaywallGate></ManagerRoute>} />
            <Route path="/deals/:id" element={<ManagerRoute><DashboardPaywallGate><DashboardLayout><DealDetail /></DashboardLayout></DashboardPaywallGate></ManagerRoute>} />
            <Route path="/log-activity" element={<ProtectedRoute><DashboardPaywallGate><DashboardLayout><QuickLog /></DashboardLayout></DashboardPaywallGate></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ManagerRoute><DashboardPaywallGate><DashboardLayout><Leaderboard /></DashboardLayout></DashboardPaywallGate></ManagerRoute>} />
            <Route path="/reviews" element={<ManagerRoute><DashboardPaywallGate><DashboardLayout><Reviews /></DashboardLayout></DashboardPaywallGate></ManagerRoute>} />
            <Route path="/rep" element={<RepRoute><DashboardPaywallGate><RepDashboard /></DashboardPaywallGate></RepRoute>} />
            <Route path="/today" element={<RepRoute><DashboardPaywallGate><Today /></DashboardPaywallGate></RepRoute>} />
            <Route path="/programs" element={<ProtectedRoute><DashboardPaywallGate><DashboardLayout><Programs /></DashboardLayout></DashboardPaywallGate></ProtectedRoute>} />
            <Route path="/programs/:id" element={<ProtectedRoute><DashboardPaywallGate><DashboardLayout><ProgramDetail /></DashboardLayout></DashboardPaywallGate></ProtectedRoute>} />
            <Route path="/wishlist" element={<ProtectedRoute><DashboardPaywallGate><DashboardLayout><WishlistPage /></DashboardLayout></DashboardPaywallGate></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><DashboardLayout><SettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
